import { LoginResponse } from "./../services/AuthService";
// src/api/HttpClient.ts
import {
  Observable,
  BehaviorSubject,
  throwError,
  defer,
  of,
  EMPTY,
} from "rxjs";
import { message } from "antd";
import { ajax, AjaxError, AjaxResponse } from "rxjs/ajax";
import {
  catchError,
  finalize,
  map,
  switchMap,
  take,
  filter,
  tap,
} from "rxjs/operators";
import type { RequestOptions } from "./types"; // keep your existing type
import { buildRequestUrl } from "./HttpHelper"; // keep your existing helper
import { APIHosts } from "@/services/reduxHelpers";
import AuthService from "@/services/AuthService";
import { saveUserProfileFromToken } from "@/utils/jwtHelper";
import i18n from "@/locales/i18n";

/* ===== Error handler for HTTP status codes ===== */
export const handleHttpError = (err: AjaxError): string => {
  let userMessage = i18n.t("errors:http_default");

  if (err.status === 400)
    userMessage = i18n.t("errors:http_400");
  else if (err.status === 401)
    userMessage = i18n.t("errors:http_401");
  else if (err.status === 403)
    userMessage = i18n.t("errors:http_403");
  else if (err.status === 404)
    userMessage = i18n.t("errors:http_404");
  else if (err.status === 409)
    userMessage = i18n.t("errors:http_409");
  else if (err.status === 429)
    userMessage = i18n.t("errors:http_429");
  return userMessage;
};

export class HttpMethod {
  static GET = "GET";
  static POST = "POST";
  static PUT = "PUT";
  static PATCH = "PATCH";
  static DELETE = "DELETE";
}

// Optional Redux store injection (kept for compatibility)
export type StoreLike = unknown;
let __storeRef: StoreLike | null = null;
let __bootstrapConfig: any | null = null;

/** Allows code to pass in the Redux store; currently unused but kept for API compatibility. */
export const injectStore = (reduxStore: StoreLike) => {
  __storeRef = reduxStore;
};

export const setBootstrapConfig = (cfg: any | null) => {
  __bootstrapConfig = cfg;
};

export const getBootstrapConfig = (): any | null => {
  return __bootstrapConfig;
};

/* ===== token storage ===== */
export const ACCESS_KEY = "auth.accessToken";
export const REFRESH_KEY = "auth.refreshToken";

const getStoredAccess = () => localStorage.getItem(ACCESS_KEY);
const setStoredAccess = (t: string | null) =>
  t ? localStorage.setItem(ACCESS_KEY, t) : localStorage.removeItem(ACCESS_KEY);
const getStoredRefresh = () => localStorage.getItem(REFRESH_KEY);
export const setStoredRefresh = (t: string | null) =>
  t
    ? localStorage.setItem(REFRESH_KEY, t)
    : localStorage.removeItem(REFRESH_KEY);

/* ===== optional refresh endpoint (leave empty to disable) ===== */
const AUTH_REFRESH_URL = `${APIHosts.Authen}/connect/token`; // e.g. `${APIHosts.Authen}/api/auth/refresh`
type RefreshResponse = { accessToken: string; refreshToken?: string };

/* ===== token subject ===== */
export const tokenSubject = new BehaviorSubject<string | null>(
  getStoredAccess()
);
let isRefreshing = false;

/* expose to login/logout */
export const setToken = (token: string | null) => {
  setStoredAccess(token);
  tokenSubject.next(token);
};

export const isAuthenticated = (): boolean => {
  const token = getStoredAccess();
  return token !== null && token !== "";
};

/* ===== core sender (overloads) ===== */
function sendHttpRequest<T = unknown>(
  url: string,
  options: RequestOptions
): Observable<T>;
function sendHttpRequest<T = unknown>(
  url: string,
  options: RequestOptions,
  isFull: true
): Observable<AjaxResponse<T>>;
function sendHttpRequest<T = unknown>(
  url: string,
  options: RequestOptions,
  isFull?: false
): Observable<T>;
function sendHttpRequest<T = unknown>(
  url: string,
  options: RequestOptions,
  isFull = false
): Observable<T | AjaxResponse<T>> {
  const {
    method = HttpMethod.GET,
    body,
    headers: customHeaders,
    createXHR,
    responseType = "json",
  } = options || {};

  const token = tokenSubject.getValue();

  // Set Accept header based on responseType
  const acceptHeader = responseType === "blob" ? "*/*" : "application/json";

  const base: Record<string, string> = {
    Accept: acceptHeader,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const headers =
    body instanceof FormData
      ? { ...base, ...(customHeaders || {}) } // let browser set boundary
      : {
          "Content-Type": "application/json",
          ...base,
          ...(customHeaders || {}),
        };

  const payload =
    body instanceof FormData
      ? body
      : body == null
      ? undefined
      : typeof body === "string"
      ? body
      : JSON.stringify(body);

  return ajax<T>({
    url,
    method,
    headers,
    body: payload as any,
    responseType: responseType as any,
    ...(createXHR ? { createXHR } : {}),
  }).pipe(map((x: AjaxResponse<T>) => (isFull ? x : (x.response as T))));
}

/* ===== refresh on 401 ===== */
export const refreshAccessToken = (): Observable<LoginResponse> => {
  const refreshToken = getStoredRefresh();
  if (!refreshToken) {
    console.error("No refresh token found. Redirecting to login...");
    message.error(i18n.t("errors:session_expired"));
    window.location.href = "/login";
    return EMPTY;
  }

  if (isRefreshing) {
    return tokenSubject.pipe(
      filter((t): t is string => !!t),
      take(1),
      map((t) => ({ accessToken: t, refreshToken }))
    );
  }

  isRefreshing = true;

  const orgId =
    localStorage.getItem("orgId") || "08dc17d2-4812-4faf-8da9-4bad064e5582";
  const clientId = localStorage.getItem("clientId") || "CXM";

  const body = new URLSearchParams({
    grant_type: "refresh_token",
    scope: "offline_access API",
    client_id: clientId,
    refresh_token: refreshToken,
    remember: "true",
    orgId,
    client_secret: "ConstruxivViewersecret",
  }).toString();

  return ajax({
    url: `${AuthService.config.authenUrl}/connect/token`,
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body,
    responseType: "json",
  }).pipe(
    map((res: AjaxResponse<any>) => {
      setToken(res.response.access_token || res.response.accessToken);
      setStoredRefresh(res.response.refresh_token || res.response.refreshToken);
      saveUserProfileFromToken(res.response.access_token || res.response.accessToken);
      return {
      accessToken: res.response.access_token || res.response.accessToken,
      refreshToken: res.response.refresh_token || res.response.refreshToken,
    }}
  ),
    catchError((error: AjaxError) => {
      if (error.status === 401 || error.status === 400) {
        setToken(null);
        setStoredRefresh(null);
        localStorage.clear();
        sessionStorage.clear();
        console.error("Refresh token expired. Redirecting to login...");
        message.error(i18n.t("errors:session_expired"));
        window.location.href = "/login";
        return EMPTY;
      }
      console.error("Refresh token error:", error);
      handleHttpError(error);
      message.error(i18n.t("errors:session_expired"));
      window.location.href = "/login";
      return EMPTY;
    }),
    finalize(() => {
      isRefreshing = false;
    })
  );
};

/* ===== high-level wrapper (overloads) ===== */
function httpRequest<T = unknown>(
  url: string,
  options?: RequestOptions
): Observable<T>;
function httpRequest<T = unknown>(
  url: string,
  options: RequestOptions & { isFull: true }
): Observable<AjaxResponse<T>>;
function httpRequest<T = unknown>(
  url: string,
  options: RequestOptions = {}
): Observable<T | AjaxResponse<T>> {
  const rUrl = buildRequestUrl(url, options.search);
  const full = options.isFull === true;

  return defer(() => tokenSubject.pipe(take(1))).pipe(
    switchMap(() =>
      full
        ? sendHttpRequest<T>(rUrl, options, true as const)
        : sendHttpRequest<T>(rUrl, options)
    ),
    catchError((error: AjaxError) => {
      if (error.status === 401) {
        setToken(null);
        setStoredRefresh(null);
        localStorage.clear();
        sessionStorage.clear();
        console.error("401 Unauthorized - Redirecting to login");
        message.error(i18n.t("errors:session_expired"));
        setTimeout(() => {
          window.location.href = "/login";
        }, 1000);
        return EMPTY;
      }
      handleHttpError(error);
      return throwError(() => error);
    })
  );
}

/* ===== public API ===== */
class HttpInterceptor {
  request<T = unknown>(
    method: string,
    url: string,
    body?: any,
    options?: RequestOptions
  ) {
    return httpRequest<T>(url, { ...options, method, body });
  }
  get<T = unknown>(url: string, options?: RequestOptions) {
    return this.request<T>(HttpMethod.GET, url, undefined, options);
  }
  post<T = unknown>(url: string, body?: any, options?: RequestOptions) {
    return this.request<T>(HttpMethod.POST, url, body, options);
  }
  put<T = unknown>(url: string, body?: any, options?: RequestOptions) {
    return this.request<T>(HttpMethod.PUT, url, body, options);
  }
  patch<T = unknown>(url: string, body?: any, options?: RequestOptions) {
    return this.request<T>(HttpMethod.PATCH, url, body, options);
  }
  delete<T = unknown>(url: string, body?: any, options?: RequestOptions) {
    return this.request<T>(HttpMethod.DELETE, url, body, options);
  }

  // upload overload A: files + extra data
  upload<T = unknown>(
    url: string,
    files: File[],
    additionalData: any = {},
    options?: RequestOptions
  ): Observable<T>;
  // upload overload B: (legacy) progress callback + FormData
  upload<T = unknown>(
    url: string,
    setProgress: (progress: number) => void,
    formData?: FormData,
    options?: RequestOptions
  ): Observable<T>;
  upload<T = unknown>(
    url: string,
    a: any,
    b?: any,
    options?: RequestOptions
  ): Observable<T> {
    // legacy signature: (url, setProgress, formData?, options?)
    if (typeof a === "function") {
      const setProgress = a as (n: number) => void;
      const formData = (b as FormData) ?? new FormData();
      const ro: RequestOptions = {
        ...options,
        createXHR: () => {
          const xhr = new XMLHttpRequest();
          xhr.upload.onprogress = (evt) => {
            if (evt.lengthComputable)
              setProgress((evt.loaded / evt.total) * 100);
          };
          return xhr;
        },
      };
      return this.post<T>(url, formData, ro);
    }

    // new signature: (url, files, additionalData?, options?)
    const files: File[] = a as File[];
    const extra = b ?? {};
    const form = new FormData();
    for (const f of files ?? []) form.append("Photos", f, f.name);
    for (const key in extra)
      if (extra[key] != null) form.append(key, extra[key]);
    return this.post<T>(url, form, { ...options });
  }
}

const HttpClient = new HttpInterceptor();
export default HttpClient;
